import { useEffect, useState } from 'react';
import {
  Box, Stack, Typography, Chip, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton,
  TextField, Select, MenuItem, FormControl, InputLabel, Skeleton,
  Avatar, Divider, Tabs, Tab, Checkbox, ListItemText, OutlinedInput
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SendIcon from '@mui/icons-material/Send';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/apiClient';
import { useAuth } from '../state/AuthContext';
import { useToast } from '../state/ToastContext';
import { SupportTicket, TicketMessage, TicketStats } from '../types';
import { parseErrorMessage } from '../utils/errorUtils';
import ConfirmationDialog from '../components/ConfirmationDialog';
import CustomButton from '../components/CustomButton';
import FormDialog from '../components/FormDialog';
import {
  FilterToggleProvider,
  FilterButton,
  FilterCollapse,
} from '../components/FilterToggle';
import { ExportMenu } from "../components/ExportMenu";

const STATUS_COLORS: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'> = {
  open: 'error',
  in_progress: 'warning',
  resolved: 'success',
  closed: 'default',
};

const PRIORITY_COLORS: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'> = {
  low: 'default',
  medium: 'primary',
  high: 'warning',
  urgent: 'error',
};

export default function TicketsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();
  const theme = useTheme();

  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [stats, setStats] = useState<TicketStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);

  // Delete dialog
  const [deleteTarget, setDeleteTarget] = useState<SupportTicket | null>(null);

  // Create ticket dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newSubject, setNewSubject] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newPriority, setNewPriority] = useState<'low' | 'medium' | 'high' | 'urgent'>('medium');
  const [creating, setCreating] = useState(false);

  // Advanced Filters
  const [statusFilter, setStatusFilter] = useState<string[]>(['open', 'in_progress']);
  const [priorityFilter, setPriorityFilter] = useState<string[]>([]);

  // Update filters when tab changes
  useEffect(() => {
    if (activeTab === 'active') setStatusFilter(['open', 'in_progress']);
    else if (activeTab === 'resolved') setStatusFilter(['resolved']);
    else if (activeTab === 'archived') setStatusFilter(['closed', 'cancelled']);
  }, [activeTab]);

  const canManageTickets = user?.role === 'master_full' || user?.role === 'dealer_admin';

  const loadTickets = async () => {
    setLoading(true);
    try {
      const [ticketData, statsData] = await Promise.all([
        api.listTickets(null, {}), // Fetch all
        api.getTicketStats(null),
      ]);
      setTickets(ticketData);
      setStats(statsData);
    } catch (err) {
      showError(parseErrorMessage(err, t('tickets.errors.load')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteClick = (ticket: SupportTicket) => {
    setDeleteTarget(ticket);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.deleteTicket(null, deleteTarget.id);
      showSuccess(t('tickets.messages.deleted', 'Ticket excluído com sucesso.'));
      setDeleteTarget(null);
      loadTickets();
    } catch (err) {
      showError(parseErrorMessage(err, t('tickets.errors.delete', 'Falha ao excluir ticket.')));
    }
  };

  const handleCreateTicket = async () => {
    if (!newSubject.trim() || !newDescription.trim()) return;
    setCreating(true);
    try {
      await api.createTicket(null, {
        subject: newSubject.trim(),
        description: newDescription.trim(),
        priority: newPriority,
      });
      showSuccess(t('tickets.messages.created'));
      setCreateOpen(false);
      setNewSubject('');
      setNewDescription('');
      setNewPriority('medium');
      loadTickets();
    } catch (err) {
      showError(parseErrorMessage(err, t('tickets.errors.create')));
    } finally {
      setCreating(false);
    }
  };

  const handleOpenTicket = async (ticket: SupportTicket) => {
    setSelectedTicket(ticket);
    setLoadingMessages(true);
    try {
      const msgs = await api.getTicketMessages(null, ticket.id);
      setMessages(msgs);
    } catch (err) {
      showError(parseErrorMessage(err, t('tickets.errors.loadMessages')));
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSendMessage = async () => {
    if (!selectedTicket || !newMessage.trim()) return;
    try {
      const msg = await api.addTicketMessage(null, selectedTicket.id, newMessage.trim(), isInternal);
      setMessages([...messages, msg]);
      setNewMessage('');
      setIsInternal(false);
    } catch (err) {
      showError(parseErrorMessage(err, t('tickets.errors.sendMessage')));
    }
  };

  const handleUpdateStatus = async (ticket: SupportTicket, newStatus: string) => {
    try {
      await api.updateTicket(null, ticket.id, { status: newStatus as SupportTicket['status'] });
      showSuccess(t('tickets.messages.updated'));
      loadTickets();
      if (selectedTicket?.id === ticket.id) {
        setSelectedTicket({ ...selectedTicket, status: newStatus as SupportTicket['status'] });
      }
    } catch (err) {
      showError(parseErrorMessage(err, t('tickets.errors.update')));
    }
  };

  return (
    <Stack spacing={3}>
      <FilterToggleProvider>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h5" fontWeight={600}>
            {t('tickets.title', 'Tickets de Suporte')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('tickets.subtitle', 'Gerencie solicitações de suporte')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <ExportMenu
            data={tickets}
            filename="tickets-export"
            columns={[
              { id: "subject", label: t('tickets.table.subject', 'Assunto') },
              { id: "status", label: t('tickets.table.status', 'Status') },
              { id: "priority", label: t('tickets.table.priority', 'Prioridade') },
              {
                id: "createdAt",
                label: t('tickets.table.createdAt', 'Criado em'),
                transform: (val) => val ? new Date(val).toLocaleDateString() : "—",
              },
            ]}
          />
          <FilterButton />
          {user?.role !== 'master_full' && (
            <CustomButton variant="primary" icon={<AddIcon />} iconPosition="left" onClick={() => setCreateOpen(true)}>
              {t('tickets.new', 'Novo Ticket')}
            </CustomButton>
          )}
          <Tooltip title={t("common.refresh")}>
            <span>
              <CustomButton
                variant="icon"
                icon={<RefreshIcon />}
                iconPosition="only"
                customSize={{ width: 36, height: 36 }}
                onClick={loadTickets}
                disabled={loading}
              />
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      <FilterCollapse>
        <Stack direction="row" spacing={2}>
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>{t('tickets.table.status', 'Status')}</InputLabel>
            <Select
              multiple
              value={statusFilter}
              onChange={(e) => setStatusFilter(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              input={<OutlinedInput label={t('tickets.table.status', 'Status')} />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={t(`tickets.status.${value}`, value)} size="small" />
                  ))}
                </Box>
              )}
            >
              {Object.keys(STATUS_COLORS).map((status) => (
                <MenuItem key={status} value={status}>
                  <Checkbox checked={statusFilter.indexOf(status) > -1} />
                  <ListItemText primary={t(`tickets.status.${status}`, status)} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>{t('tickets.table.priority', 'Prioridade')}</InputLabel>
            <Select
              multiple
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              input={<OutlinedInput label={t('tickets.table.priority', 'Prioridade')} />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={t(`tickets.priority.${value}`, value)} size="small" />
                  ))}
                </Box>
              )}
            >
              {Object.keys(PRIORITY_COLORS).map((priority) => (
                <MenuItem key={priority} value={priority}>
                  <Checkbox checked={priorityFilter.indexOf(priority) > -1} />
                  <ListItemText primary={t(`tickets.priority.${priority}`, priority)} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </FilterCollapse>
      </FilterToggleProvider>

      {/* Stats Cards */}
      {stats && (
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <Card variant="outlined" sx={{ minWidth: 120 }}>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">{t('tickets.stats.open', 'Abertos')}</Typography>
              <Typography variant="h5" fontWeight={600} color="error.main">{stats.open_count}</Typography>
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ minWidth: 120 }}>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">{t('tickets.stats.inProgress', 'Em andamento')}</Typography>
              <Typography variant="h5" fontWeight={600} color="warning.main">{stats.in_progress_count}</Typography>
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ minWidth: 120 }}>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">{t('tickets.stats.resolved', 'Resolvidos')}</Typography>
              <Typography variant="h5" fontWeight={600} color="success.main">{stats.resolved_count}</Typography>
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ minWidth: 120 }}>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">{t('tickets.stats.urgent', 'Urgentes')}</Typography>
              <Typography variant="h5" fontWeight={600} color="error.main">{stats.urgent_count}</Typography>
            </CardContent>
          </Card>
        </Stack>
      )}

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{ px: 2 }}
          >
            <Tab
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <span>{t('tickets.tabs.active', 'Ativos')}</span>
                  <Chip
                    label={tickets.filter(t => ['open', 'in_progress'].includes(t.status)).length}
                    size="small"
                    color="error"
                  />
                </Stack>
              }
              value="active"
            />
            <Tab
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <span>{t('tickets.tabs.resolved', 'Resolvidos')}</span>
                  <Chip
                    label={tickets.filter(t => t.status === 'resolved').length}
                    size="small"
                    color="success"
                  />
                </Stack>
              }
              value="resolved"
            />
            <Tab
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <span>{t('tickets.tabs.archived', 'Arquivados')}</span>
                  <Chip
                    label={tickets.filter(t => ['closed', 'cancelled'].includes(t.status)).length}
                    size="small"
                    color="default"
                  />
                </Stack>
              }
              value="archived"
            />
          </Tabs>
        </Box>
        <CardContent>
          {(() => {
            const filteredTickets = tickets.filter(ticket => {
              // Status filter
              if (statusFilter.length > 0 && !statusFilter.includes(ticket.status)) return false;
              // Priority filter
              if (priorityFilter.length > 0 && !priorityFilter.includes(ticket.priority)) return false;

              return true;
            });

            if (loading) {
              return <Skeleton height={200} />;
            }

            if (filteredTickets.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary" align="center" py={4}>
                  {t('tickets.empty', 'Nenhum ticket encontrado nesta aba.')}
                </Typography>
              );
            }

            return (
              <TableContainer component={Box}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell>{t('tickets.table.subject', 'Assunto')}</TableCell>
                      <TableCell>{t('tickets.table.tenant', 'Cliente')}</TableCell>
                      <TableCell>{t('tickets.table.status', 'Status')}</TableCell>
                      <TableCell>{t('tickets.table.priority', 'Prioridade')}</TableCell>
                      <TableCell>{t('tickets.table.createdAt', 'Criado em')}</TableCell>
                      <TableCell align="right">{t('common.actions', 'Ações')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTickets.map((ticket) => (
                      <TableRow key={ticket.id} hover>
                        <TableCell>
                          <Stack>
                            <Typography variant="body2" fontWeight={500}>{ticket.subject}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {ticket.createdByName}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip label={ticket.tenantName || ticket.tenantCode} size="small" />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={t(`tickets.status.${ticket.status}`, ticket.status)}
                            size="small"
                            color={STATUS_COLORS[ticket.status] || 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={t(`tickets.priority.${ticket.priority}`, ticket.priority)}
                            size="small"
                            color={PRIORITY_COLORS[ticket.priority] || 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(ticket.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Tooltip title={t("common.view")}>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleOpenTicket(ticket)}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            {(canManageTickets || (ticket.createdByUserId === user?.id && ticket.status === 'open')) && (
                            <Tooltip title={t("common.delete")}>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteClick(ticket)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            );
          })()}
        </CardContent>
      </Card>

      {/* Create Ticket Dialog */}
      <FormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreateTicket}
        title={t('tickets.dialog.createTitle', 'Novo Ticket')}
        submitText={t('tickets.dialog.create', 'Criar Ticket')}
        loading={creating}
        disableSubmit={!newSubject.trim() || !newDescription.trim()}
        maxWidth="sm"
        disableBackdropClick
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t('tickets.form.subject', 'Assunto')}
            value={newSubject}
            onChange={(e) => setNewSubject(e.target.value)}
            fullWidth
            autoFocus
          />
          <TextField
            label={t('tickets.form.description', 'Descrição')}
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            fullWidth
            multiline
            rows={4}
          />
          <FormControl fullWidth>
            <InputLabel>{t('tickets.form.priority', 'Prioridade')}</InputLabel>
            <Select
              value={newPriority}
              label={t('tickets.form.priority', 'Prioridade')}
              onChange={(e) => setNewPriority(e.target.value as SupportTicket['priority'])}
            >
              <MenuItem value="low">{t('tickets.priority.low', 'Baixa')}</MenuItem>
              <MenuItem value="medium">{t('tickets.priority.medium', 'Média')}</MenuItem>
              <MenuItem value="high">{t('tickets.priority.high', 'Alta')}</MenuItem>
              <MenuItem value="urgent">{t('tickets.priority.urgent', 'Urgente')}</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </FormDialog>

      {/* Ticket Detail Dialog */}
      <FormDialog
        open={!!selectedTicket}
        onClose={() => setSelectedTicket(null)}
        title={selectedTicket?.subject || ''}
        hideSubmit
        cancelText={t('common.close', 'Fechar')}
        maxWidth="md"
        disableBackdropClick
        headerContent={
          selectedTicket ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={t(`tickets.status.${selectedTicket.status}`, selectedTicket.status)}
                size="small"
                color={STATUS_COLORS[selectedTicket.status] || 'default'}
              />
              <Chip
                label={t(`tickets.priority.${selectedTicket.priority}`, selectedTicket.priority)}
                size="small"
                color={PRIORITY_COLORS[selectedTicket.priority] || 'default'}
                variant="outlined"
              />
            </Stack>
          ) : undefined
        }
      >
        {selectedTicket && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* Ticket Info */}
            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="body2">{selectedTicket.description}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {t('tickets.detail.createdBy', 'Criado por')}: {selectedTicket.createdByName} • {new Date(selectedTicket.createdAt).toLocaleString()}
              </Typography>
            </Box>

            {/* Status Update (for admins) */}
            {canManageTickets && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Typography variant="body2" sx={{ alignSelf: 'center' }}>
                  {t('tickets.detail.changeStatus', 'Alterar status')}:
                </Typography>
                {['open', 'in_progress', 'resolved', 'closed'].map((status) => (
                  <Chip
                    key={status}
                    label={t(`tickets.status.${status}`, status)}
                    size="small"
                    color={selectedTicket.status === status ? STATUS_COLORS[status] : 'default'}
                    variant={selectedTicket.status === status ? 'filled' : 'outlined'}
                    onClick={() => handleUpdateStatus(selectedTicket, status)}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Stack>
            )}

            <Divider />

            {/* Messages */}
            <Typography variant="subtitle2">{t('tickets.detail.messages', 'Mensagens')}</Typography>

            <Stack spacing={1} sx={{ maxHeight: 300, overflowY: 'auto' }}>
              {loadingMessages ? (
                <Skeleton height={60} />
              ) : messages.length === 0 ? (
                <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                  {t('tickets.detail.noMessages', 'Nenhuma mensagem ainda')}
                </Typography>
              ) : (
                messages.map((msg) => (
                  <Box
                    key={msg.id}
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: msg.isInternal ? alpha(theme.palette.warning.main, 0.1) : 'action.hover',
                      borderLeft: msg.isInternal ? `3px solid ${theme.palette.warning.main}` : 'none',
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
                          {msg.userName?.charAt(0) || '?'}
                        </Avatar>
                        <Typography variant="body2" fontWeight={500}>{msg.userName}</Typography>
                        {msg.isInternal && (
                          <Chip label={t('tickets.detail.internal', 'Interno')} size="small" color="warning" sx={{ height: 18 }} />
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(msg.createdAt).toLocaleString()}
                      </Typography>
                    </Stack>
                    <Typography variant="body2">{msg.message}</Typography>
                  </Box>
                ))
              )}
            </Stack>

            {/* New Message */}
            <Stack direction="row" spacing={1}>
              <TextField
                placeholder={t('tickets.detail.writeMessage', 'Escreva uma mensagem...')}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                fullWidth
                size="small"
                onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
              />
              {canManageTickets && (
                <CustomButton
                  variant="secondary"
                  onClick={() => setIsInternal(!isInternal)}
                >
                  {t('tickets.detail.internal', 'Interno')}
                </CustomButton>
              )}
              <Tooltip title={t("common.send")}>
                <span>
                  <CustomButton
                    variant="icon"
                    icon={<SendIcon />}
                    iconPosition="only"
                    customSize={{ width: 36, height: 36 }}
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim()}
                  />
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        )}
      </FormDialog>
      <ConfirmationDialog
        open={!!deleteTarget}
        title={t('tickets.dialog.deleteTitle', 'Excluir Ticket')}
        content={t('tickets.confirmDelete', 'Tem certeza que deseja excluir este ticket? Esta ação não pode ser desfeita.')}
        confirmText={t('common.delete', 'Excluir')}
        onConfirm={handleConfirmDelete}
        onClose={() => setDeleteTarget(null)}
        severity="error"
      />
    </Stack>
  );
}
